import { useState, useRef } from 'react'
import { ImagePlus, Loader2, Play, Send, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { api } from '@/api/client'
import { cn } from '@/lib/utils'

type Filter = 'player' | 'blur' | 'none'

/**
 * Página OCULTA (sem link em menu) — /broadcast/card-builder.
 * Cria template de utility com header de IMAGEM + até 2 botões de URL.
 * A imagem fica cravada no template aprovado pela Meta (não dá pra trocar depois).
 */
export default function CardBuilder() {
  const [name, setName] = useState('')
  const [headerTitle, setHeaderTitle] = useState('Seu vídeo já está pronto')
  const [body, setBody] = useState('Vini, terminei de preparar o seu vídeo. Toque abaixo para ver quando quiser.')
  const [filter, setFilter] = useState<Filter>('player')
  const [imageUrl, setImageUrl] = useState('')
  const [uploading, setUploading] = useState(false)
  const [b1Text, setB1Text] = useState('Abrir vídeo')
  const [b1Url, setB1Url] = useState('')
  const [b2Text, setB2Text] = useState('Assistir agora')
  const [b2Url, setB2Url] = useState('')
  const [creating, setCreating] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const hasVar = body.includes('{{1}}')

  async function handleFile(f: File | undefined) {
    if (!f) return
    setUploading(true)
    try {
      const r = await api.upload(f, { filterType: filter })
      if (!r?.url) throw new Error('sem url')
      setImageUrl(r.url)
      toast.success('Imagem enviada')
    } catch (e: any) {
      toast.error(e?.message || 'Falha no upload da imagem')
    } finally {
      setUploading(false)
    }
  }

  async function handleCreate() {
    if (!name.trim() || !body.trim()) { toast.error('Preencha nome e mensagem'); return }
    if (!imageUrl) { toast.error('Envie a imagem do header'); return }
    if (!headerTitle.trim()) { toast.error('Preencha o título (obrigatório com imagem)'); return }
    setCreating(true)
    try {
      const r: any = await api.post('/broadcast/templates', {
        name, body, imageUrl, headerTitle,
        buttonText: b1Text, buttonUrl: b1Url,
        button2Text: b2Text, button2Url: b2Url,
        example: 'Maria',
      })
      toast.success(`"${r.name}": ${r.approved} aprovadas / ${r.rejected} rejeitadas de ${r.total} páginas`)
      if (r.rejected > 0 && r.reasons && Object.keys(r.reasons).length) {
        const reasons = Object.entries(r.reasons).map(([k, v]) => `${k} (${v})`).join(', ')
        toast.warning(`Motivos de rejeição: ${reasons}`, { duration: 8000 })
      }
      setName('')
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao criar template')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <div className="mb-4">
        <h1 className="text-lg font-semibold">Card Builder <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-500">oculto</span></h1>
        <p className="text-xs text-muted-foreground">Template de utility com imagem no topo + até 2 botões. A imagem fica fixa no template aprovado — trocar a foto exige criar um template novo.</p>
      </div>

      <div className="grid gap-6 md:grid-cols-[1fr_320px]">
        {/* Formulário */}
        <div className="space-y-4 rounded-xl border bg-card p-4">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Nome do template</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="video_pronto" className="mt-1" />
            <p className="mt-1 text-[10px] text-muted-foreground">Só letras minúsculas, números e _</p>
          </div>

          {/* Imagem */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Imagem (header)</Label>
            <div className="mt-1 flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => handleFile(e.target.files?.[0])} />
              <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
                {uploading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="mr-1 h-3.5 w-3.5" />}
                {imageUrl ? 'Trocar imagem' : 'Selecionar imagem'}
              </Button>
              {imageUrl && (
                <button onClick={() => setImageUrl('')} className="text-muted-foreground hover:text-destructive" title="Remover">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="mt-2 flex gap-1.5">
              {(['player', 'blur', 'none'] as Filter[]).map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={cn(
                    'rounded-md border px-2 py-1 text-[11px] capitalize',
                    filter === f ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground',
                  )}
                >
                  {f === 'player' ? 'Player (vídeo falso)' : f === 'blur' ? 'Blur' : 'Sem filtro'}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">O filtro é aplicado no envio. Reenvie a imagem se trocar o filtro.</p>
          </div>

          {/* Título (header text — negrito) */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Título (negrito)</Label>
            <Input value={headerTitle} onChange={e => setHeaderTitle(e.target.value)} placeholder="Seu vídeo já está pronto" maxLength={60} className="mt-1" />
            <p className="mt-1 text-[10px] text-muted-foreground">Obrigatório com imagem. Até 60 caracteres. É o texto do header.</p>
          </div>

          {/* Corpo */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Mensagem</Label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
            <p className="mt-1 text-[10px] text-muted-foreground">
              {hasVar ? <span className="text-emerald-500">✓ usa {'{{1}}'} (nome do contato)</span> : 'sem variável de nome — use {{1}} para inserir o nome'}
            </p>
          </div>

          {/* Botões */}
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Botão 1</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input value={b1Text} onChange={e => setB1Text(e.target.value)} placeholder="Abrir vídeo" maxLength={20} />
              <Input value={b1Url} onChange={e => setB1Url(e.target.value)} placeholder="https://..." />
            </div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Botão 2 (opcional)</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input value={b2Text} onChange={e => setB2Text(e.target.value)} placeholder="Assistir agora" maxLength={20} />
              <Input value={b2Url} onChange={e => setB2Url(e.target.value)} placeholder="https://..." />
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <p className="text-[10px] text-muted-foreground">Criado em todas as suas páginas FB</p>
            <Button onClick={handleCreate} disabled={creating || uploading}>
              {creating ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Criando...</> : <><Send className="mr-1 h-4 w-4" /> Criar e enviar p/ revisão</>}
            </Button>
          </div>
        </div>

        {/* Preview */}
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Preview</Label>
          <div className="mt-1 flex items-end gap-2">
            <div className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500" />
            <div className="w-full overflow-hidden rounded-2xl bg-muted">
              {/* Imagem */}
              <div className="relative flex aspect-[1.6] items-center justify-center bg-neutral-800">
                {imageUrl ? (
                  <img src={imageUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <ImagePlus className="h-8 w-8 text-neutral-500" />
                )}
                {filter === 'player' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90">
                      <Play className="h-6 w-6 translate-x-0.5 fill-neutral-800 text-neutral-800" />
                    </div>
                  </div>
                )}
              </div>
              {/* Título + corpo */}
              <div className="px-4 pt-3">
                {headerTitle && <div className="text-[15px] font-bold text-neutral-900 dark:text-white">{headerTitle}</div>}
                <div className="mt-0.5 text-sm text-neutral-600 dark:text-neutral-300">
                  {body.replace('{{1}}', 'Vini') || 'Sua mensagem aqui...'}
                </div>
              </div>
              {/* Botões */}
              <div className="space-y-1 p-2">
                {b1Text && (
                  <div className="rounded-xl bg-white py-2.5 text-center text-sm font-semibold text-neutral-800 shadow-sm">{b1Text}</div>
                )}
                {b2Text && b2Url && (
                  <div className="rounded-xl bg-white py-2.5 text-center text-sm font-semibold text-neutral-800 shadow-sm">{b2Text}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
